import { CommonModule } from '@angular/common';
import { Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { filter, Subject, takeUntil } from 'rxjs';
import { AuditHistoryDialogComponent } from './audit-history-dialog.component';
import Keycloak from 'keycloak-js';

interface AuditContext {
    entityType: string;
    entityId?: string;
    scope?: string;
    title: string;
}

@Component({
    selector: 'app-audit-history-button',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
    templateUrl: './audit-history-button.component.html',
    styleUrls: ['./audit-history-button.component.scss']
})
export class AuditHistoryButtonComponent implements OnInit, OnDestroy {
    @Input() inline = false;
    isAdmin = false;

    @HostBinding('class.inline-mode')
    get inlineModeClass(): boolean {
        return this.inline;
    }

    context: AuditContext | null = null;
    private readonly destroy$ = new Subject<void>();

    private readonly routeMap: Record<string, string> = {
        patients: 'Patient',
        products: 'Product',
        'product-kits': 'ProductKit',
        mutuals: 'Mutual',
        users: 'Users',
        consultations: 'Consultations',
        entries: 'Entry',
        acts: 'MedicalAct',
        constants: 'Constants',
        analysisrequest: 'AnalysisRequest',
        analysisresults: 'AnalysisResult',
        hospitalizationnotice: 'HospitalizationNotice',
        hospitalization: 'Hospitalization',
        hospitalizationfollow: 'SurveillanceSheet',
        treatmentrecord: 'TreatmentRecord',
        radiologyreport: 'RadiologyReport',
        billing: 'Invoice',
        mutualsinvoice: 'MutualInvoice',
        admissions: 'Entry',
        planning: 'Appointment',
        appointment: 'AppointmentRequest',
        queue: 'Ticket'
    };
    private readonly entityTitleMap: Record<string, { singular: string; plural: string }> = {
        Patient: { singular: 'la fiche patient', plural: 'fiches patient' },
        Entry: { singular: "l'admission", plural: 'admissions' },
        Appointment: { singular: 'le rendez-vous', plural: 'rendez-vous' },
        AppointmentRequest: { singular: 'la demande de rendez-vous', plural: 'demandes de rendez-vous' },
        AnalysisResult: { singular: "le resultat d'analyse", plural: "resultats d'analyse" },
        AnalysisRequest: { singular: "la demande d'analyse", plural: "demandes d'analyse" },
        Hospitalization: { singular: "l'hospitalisation", plural: 'hospitalisations' },
        HospitalizationNotice: { singular: "l'avis d'hospitalisation", plural: "avis d'hospitalisation" },
        Ticket: { singular: 'le ticket', plural: 'tickets' },
        Consultations: { singular: 'la consultation', plural: 'consultations' },
        Invoice: { singular: 'la facture', plural: 'factures' },
        MutualInvoice: { singular: 'la facture mutuelle', plural: 'factures mutuelles' },
        StockTimeline: { singular: "l'historique du stock", plural: 'historiques du stock' },
        ProductKit: { singular: 'le kit de produits', plural: 'kits de produits' },
        Supplier: { singular: 'le fournisseur', plural: 'fournisseurs' }
    };

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private keycloak: Keycloak
    ) {}

    ngOnInit(): void {
        this.isAdmin = this.keycloak.hasRealmRole('ADMIN');
        this.resolveContext();
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => this.resolveContext());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openHistory(): void {
        if (!this.context) {
            return;
        }

        this.dialog.open(AuditHistoryDialogComponent, {
            data: this.context,
            width: '96vw',
            maxWidth: '1200px',
            panelClass: 'audit-history-dialog-panel'
        });
    }

    private resolveContext(): void {
        const fullUrl = this.router.url;
        const route = fullUrl.split('?')[0];
        const segments = route.split('/').filter(Boolean);

        if (!segments.length) {
            this.context = null;
            return;
        }

        const normalizedSegments = segments.map((segment) => segment.toLowerCase());
        const rootSegment = normalizedSegments[0];
        const secondSegment = normalizedSegments[1];
        const thirdSegment = normalizedSegments[2];
        const queryParams = this.collectRouteQueryParams(this.activatedRoute);

        if (rootSegment === 'patients' && secondSegment === 'details') {
            const allParams = this.collectRouteParams(this.activatedRoute);
            const patientId = allParams['id'];
            const tab = (queryParams['tab'] || 'all').toLowerCase();
            const subTab = (queryParams['subTab'] || '').toLowerCase();

            if (!patientId) {
                this.context = null;
                return;
            }

            this.context = {
                entityType: 'PatientTimeline',
                entityId: patientId,
                scope: tab,
                title: this.resolvePatientTimelineTitle(tab, subTab)
            };
            return;
        }

        if (rootSegment === 'planning' && secondSegment === 'request') {
            this.context = {
                entityType: 'AppointmentRequest',
                title: 'Historique des demandes de rendez-vous'
            };
            return;
        }

        if (rootSegment === 'admissions' && secondSegment && /^\d+$/.test(secondSegment)) {
            const allParams = this.collectRouteParams(this.activatedRoute);
            const entryId = allParams['id'] || secondSegment;
            this.context = {
                entityType: 'EntryTimeline',
                entityId: entryId,
                title: `Historique complet de l'admission`
            };
            return;
        }

        if (rootSegment === 'hospitalization' && secondSegment && /^\d+$/.test(secondSegment)) {
            const allParams = this.collectRouteParams(this.activatedRoute);
            const hospitalizationId = allParams['id'] || secondSegment;
            this.context = {
                entityType: 'HospitalizationTimeline',
                entityId: hospitalizationId,
                title: `Historique complet de l'hospitalisation`
            };
            return;
        }

        if (
            rootSegment === 'analysisresults'
            && secondSegment
            && (secondSegment === 'details' || secondSegment === 'entry')
            && thirdSegment
            && /^\d+$/.test(thirdSegment)
        ) {
            const allParams = this.collectRouteParams(this.activatedRoute);
            const requestId = allParams['id'] || thirdSegment;
            this.context = {
                entityType: 'AnalysisResultsTimeline',
                entityId: requestId,
                title: 'Historique des resultats de la demande d analyse'
            };
            return;
        }

        if (rootSegment === 'stock') {
            if (secondSegment === 'create') {
                this.context = {
                    entityType: 'StockTimeline',
                    scope: 'stock-entry',
                    title: 'Historique des entrées du grand stock'
                };
            } else if (secondSegment === 'inventory' || secondSegment === 'inventories') {
                this.context = {
                    entityType: 'StockTimeline',
                    scope: 'inventory-grand',
                    title: "Historique inventaire grand stock"
                };
            } else {
                this.context = {
                    entityType: 'StockTimeline',
                    scope: 'grand-stock',
                    title: 'Historique du grand stock'
                };
            }
            return;
        }

        if (rootSegment === 'pharmacie') {
            if (secondSegment === 'inventory' || secondSegment === 'inventories') {
                this.context = {
                    entityType: 'StockTimeline',
                    scope: 'inventory-pharmacie',
                    title: "Historique inventaire pharmacie"
                };
                return;
            }
            if (secondSegment === 'stock') {
                this.context = {
                    entityType: 'StockTimeline',
                    scope: 'pharmacie',
                    title: 'Historique du stock de pharmacie'
                };
                return;
            }
        }

        if (rootSegment === 'laboratoire') {
            if (secondSegment === 'inventory' || secondSegment === 'inventories') {
                this.context = {
                    entityType: 'StockTimeline',
                    scope: 'inventory-laboratoire',
                    title: "Historique inventaire laboratoire"
                };
                return;
            }
            if (secondSegment === 'stock') {
                this.context = {
                    entityType: 'StockTimeline',
                    scope: 'laboratoire',
                    title: 'Historique du stock de laboratoire'
                };
                return;
            }
        }

        let entityType = this.routeMap[rootSegment];

        if (!entityType && rootSegment === 'settings') {
            entityType = this.resolveSettingsEntity(secondSegment);
        }

        if (!entityType) {
            this.context = null;
            return;
        }

        if (entityType === 'AnalysisTimeline') {
            this.context = {
                entityType,
                title: 'Historique complet des analyses'
            };
            return;
        }

        const allParams = this.collectRouteParams(this.activatedRoute);
        const entityId = allParams['id'] || allParams['code'] || allParams['patientId'] || allParams['insuranceId'];

        const title = entityId
            ? `Historique complet de ${this.resolveEntityTitle(entityType, 'singular')}`
            : `Historique complet des ${this.resolveEntityTitle(entityType, 'plural')}`;

        this.context = {
            entityType,
            entityId,
            title
        };
    }

    private collectRouteParams(route: ActivatedRoute): Record<string, string> {
        const params: Record<string, string> = {};
        let current: ActivatedRoute | null = route;

        while (current) {
            Object.assign(params, current.snapshot.params as Record<string, string>);
            current = current.firstChild;
        }

        return params;
    }

    private collectRouteQueryParams(route: ActivatedRoute): Record<string, string> {
        const params: Record<string, string> = {};
        let current: ActivatedRoute | null = route;

        while (current) {
            Object.assign(params, current.snapshot.queryParams as Record<string, string>);
            current = current.firstChild;
        }

        return params;
    }

    private resolveSettingsEntity(secondSegment: string | undefined): string | null {
        if (!secondSegment) {
            return null;
        }

        if (secondSegment === 'motifs-admission') return 'AdmissionMotif';
        if (secondSegment === 'service-medical') return 'MedicalServiceChoice';
        if (secondSegment === 'analysis') return 'AnalysisTimeline';
        if (secondSegment === 'service-catalog') return 'ServiceCatalog';
        if (secondSegment === 'suppliers') return 'Supplier';
        if (secondSegment === 'reagents') return 'Reagent';

        return null;
    }

    private resolveEntityTitle(entityType: string, form: 'singular' | 'plural'): string {
        const mapping = this.entityTitleMap[entityType];
        if (mapping) {
            return mapping[form];
        }
        return entityType;
    }

    private resolvePatientTimelineTitle(tab: string, subTab: string): string {
        switch (tab) {
            case 'admissions':
                return "Historique complet des admissions du patient";
            case 'constants':
                return "Historique complet des constantes du patient";
            case 'consultations':
                return "Historique complet des consultations du patient";
            case 'billing':
                return "Historique complet des factures du patient";
            case 'analysis':
                if (subTab === 'results') {
                    return "Historique complet des resultats d'analyses du patient";
                }
                if (subTab === 'requests') {
                    return "Historique complet des demandes d'analyses du patient";
                }
                return "Historique complet des analyses du patient";
            case 'hospitalization':
                if (subTab === 'hospitalizations') {
                    return "Historique complet des hospitalisations du patient";
                }
                if (subTab === 'notice') {
                    return "Historique complet des avis d'hospitalisation du patient";
                }
                return "Historique complet des hospitalisations du patient";
            case 'radiology':
                if (subTab === 'result') {
                    return "Historique complet des resultats de radiologie du patient";
                }
                if (subTab === 'bulletin') {
                    return "Historique complet des bulletins de radiologie du patient";
                }
                return "Historique complet des radiologies du patient";
            case 'patient':
            case 'all':
            default:
                return 'Historique complet du patient';
        }
    }
}
